import styled from "styled-components"
import { useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import useApi from "../../../hooks/useApi"
import { qk } from "../../../utils/queryKeys"
import useTranslation from "../../../hooks/useTranslation"
import useFormatting from "../../../hooks/useFormatting"
import useLocale from "../../../hooks/useLocale"
import Loader from "../../generic/Loader"
import { Button } from '../../generic/Core'
import ViewAll from "../ViewAll"
import { FlexBox } from "../../generic/Layout"
import Image from "../../image/Image"
import { Grid, HeaderCell, DataRow, Cell, EmptyCell, ScrollContainer } from "./tableGridStyles.jsx"
import { WidgetTitle, widgetFont } from "../widgetTypography"

// The title with "View all" at its right, as on the analytics cards
const TitleRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.5rem;

    h2 { margin: 0; min-width: 0; }
`

const CardContent = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;

    a:hover {
        text-decoration: underline;
    }
`

const CompactBadge = styled.div`
    display: none;

    @media (max-width: 480px) {
        display: flex;
        position: absolute;
        inset: 0;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        cursor: pointer;
        background: ${p => p.theme.card.background};
    }
`

const CompactCount = styled.span`
    font-size: 2.5rem;
    font-weight: 800;
    line-height: 1;
    color: ${p => p.theme.font.color};
`

const CompactLabel = styled.span`
    ${widgetFont.label}
    font-weight: 600;
    color: ${p => p.theme.font.label};
    text-align: center;
`

const ListScroll = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
`

// Legacy list-mode styled components
const Value = styled.span`
    margin-left: 1rem;
    font-weight: ${({ $red }) => $red ? '800' : '600'};
    color: ${({ theme, $red }) => $red ? theme.emotive.error.color : theme.font.color};
`
const ValueDecorator = styled.span`
    margin-left: 0.25rem;
    font-style: italic;
    font-weight: ${({ $red }) => $red ? '700' : '500'};
    font-size: var(--font-XXS);
    color: ${({ theme, $red }) => $red ? theme.emotive.error.color : theme.font.color};
`

const ListWidget = ({ type, data, labels }) => {
    const { getMe } = useApi()
    const { formatString } = useFormatting()
    const { lf } = useLocale()
    const navigate = useNavigate()

    const columns = data?.columns ?? null
    // `path` is where "show all" goes, and it may carry a query string — a widget
    // that shows one slice of a list sends the user to the tab holding that slice.
    // A record's own url never does, so rows and the create button are built off
    // `rowPath`, which is the same path without the question mark.
    const linkPath = data?.path
    const rowLinkPath = data?.rowPath ?? data?.path
    const newPath = `${rowLinkPath}/new`

    const getData = async () => {
        const resource = data.serverFilter
            ? `${data.entity}?filter=${encodeURIComponent(data.serverFilter)}`
            : `${data.entity}`
        const response = await getMe(resource)
        if (Array.isArray(response?.data)) return response.data
        // Server returns {} (no data key) when the collection is empty
        if (response && !response.status && !response.error) return []
        throw new Error(`Failed to load ${resource}`)
    }

    const result = useQuery({
        queryKey: qk.listWidget(data.entity, data.serverFilter),
        queryFn: getData,
        keepPreviousData: true
    })

    const { t } = useTranslation()

    const formatCell = (value, format) => {
        if (value == null) return '—'
        if (format === 'amount') return lf(value, 2, 2)
        if (format === 'integer') return lf(value, 0, 0)
        if (format === 'number') return lf(value, 2, 2)
        if (format === 'date' || format === 'datetime') return formatString(value, format)
        return String(value)
    }

    const colTemplate = useMemo(
        () => columns ? columns.map(c => c.width || '1fr').join(' ') : null,
        [columns]
    )

    if (result.isLoading) return <Loader />
    if (result.isError) return <span>Error: Couldn't load!</span>

    if (result.isSuccess) {
        if (!Array.isArray(result.data)) return <span>Error: Couldn't load!</span>

        const isEmpty = result.data.length === 0

        // ── Grid / table mode ──────────────────────────────────────────────
        if (columns) {
            return (
                <CardContent>
                    <TitleRow>
                        <WidgetTitle as="h2">{t(labels.title)}</WidgetTitle>
                        <ViewAll to={linkPath} />
                    </TitleRow>
                    <ScrollContainer>
                        <Grid $cols={colTemplate}>
                            {columns.map(col => (
                                <HeaderCell key={col.field}>{t(col.label ?? col.labelKey)}</HeaderCell>
                            ))}
                            {isEmpty ? (
                                <EmptyCell>
                                    <span>{t(labels.emptyDescription)}</span>
                                    <Button $shadow={true} $variant='primary'>
                                        <Link to={newPath}>{t(labels.emptyAction)}</Link>
                                    </Button>
                                </EmptyCell>
                            ) : (
                                result.data.map((row, ri) => (
                                    <DataRow
                                        key={row.id ?? ri}
                                        $clickable={!!linkPath}
                                        onClick={rowLinkPath ? () => navigate(`${rowLinkPath}/${row.id}`) : undefined}
                                    >
                                        {columns.map(col => (
                                            <Cell key={col.field} data-label={t(col.label ?? col.labelKey)}>{formatCell(row[col.field], col.format)}</Cell>
                                        ))}
                                    </DataRow>
                                ))
                            )}
                        </Grid>
                    </ScrollContainer>
                    {data.compact && (
                        <CompactBadge onClick={() => navigate(linkPath)}>
                            <CompactCount>{result.data.length}</CompactCount>
                            <CompactLabel>{t(labels.title)}</CompactLabel>
                        </CompactBadge>
                    )}
                </CardContent>
            )
        }

        // ── Legacy list mode ───────────────────────────────────────────────
        return (
            <CardContent>
                <TitleRow>
                    <WidgetTitle as="h2">{t(labels.title)}</WidgetTitle>
                    <ViewAll to={linkPath} />
                </TitleRow>
                <ListScroll>
                    <ul>
                        {result.data.map((e, i) => (
                            <li style={{ paddingBottom: '0.5rem' }} key={e['id']}>
                                <FlexBox>
                                    {data.imagePath && <Image path={e[data.imagePath]} height="1.5rem" margin='0 0.5rem 0 0' />}
                                    <Link to={`${rowLinkPath}/${e['id']}`}>{e[data.field]}</Link>
                                    {data?.value &&
                                        <>
                                            <Value $red={e[data.value] < 0}>{formatString(e[data.value], data.valueType, e)}</Value>
                                            <ValueDecorator $red={e[data.value] < 0}>{e[data.valueDecorator]}</ValueDecorator>
                                        </>}
                                </FlexBox>
                            </li>
                        ))}
                    </ul>
                    {isEmpty && (
                        <>
                            <p>{t(labels.emptyDescription)}</p>
                            <Button $shadow={true} $variant='primary'>
                                <Link to={newPath}>{t(labels.emptyAction)}</Link>
                            </Button>
                        </>
                    )}
                </ListScroll>
            </CardContent>
        )
    }
}

export default ListWidget
